/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus, InteractionRequiredAuthError } from '@azure/msal-browser';
import type { User, Role } from '../types';
import { loginRequest } from '../config/auth';

const API_BASE = import.meta.env.VITE_API_URL || '';

function getMockUser(): User {
  return {
    email: import.meta.env.VITE_LOCAL_USER_EMAIL || 'dev@localhost',
    name: import.meta.env.VITE_LOCAL_USER_NAME || 'Local Developer',
    role: (import.meta.env.VITE_LOCAL_USER_ROLE as Role) || 'admin',
  };
}

function isAuthBypassed(): boolean {
  return import.meta.env.VITE_AUTH_BYPASS === 'true';
}

// Retry transient failures (network errors, 5xx); 2xx and any 4xx are authoritative.
async function fetchWithRetry(url: string, init: RequestInit, attempts = 3): Promise<Response> {
  for (let attempt = 1; ; attempt++) {
    try {
      const response = await fetch(url, init);
      if (response.status < 500 || attempt >= attempts) {
        return response;
      }
    } catch (err) {
      if (attempt >= attempts) throw err;
    }
    await new Promise(resolve => setTimeout(resolve, 500 * attempt));
  }
}

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  getAccessToken: () => Promise<string | null>;
  signIn: () => void;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Shared across concurrent InteractionRequiredAuthError handlers (e.g. parallel
// queries): only the first triggers acquireTokenRedirect — a second call would
// throw BrowserAuthError interaction_in_progress and flash an error before
// navigation. Parked callers reject if the redirect itself fails.
let redirectPromise: Promise<void> | null = null;

function MsalAuthProvider({ children }: { children: ReactNode }) {
  const { instance, accounts, inProgress } = useMsal();
  const isMsalAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (isAuthBypassed()) return 'mock-token';

    const account = accounts[0];
    if (!account) return null;

    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account,
      });
      return response.idToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        // Expired session — MSAL's documented fallback is interactive re-auth
        if (!redirectPromise) {
          redirectPromise = instance.acquireTokenRedirect({ ...loginRequest, account });
          redirectPromise.catch((redirectErr) => {
            console.error('[AuthContext] Redirect failed:', redirectErr);
            redirectPromise = null;
          });
        }
        // Park callers on the redirect: success navigates away; failure rejects
        // them into normal request errors instead of hanging forever
        const pending = redirectPromise;
        return new Promise<string | null>((_, reject) => { pending.catch(reject); });
      }
      // Transient failure — let the caller fail rather than send unauthenticated
      throw err;
    }
  }, [instance, accounts]);

  // Fetch user profile from /me after MSAL authentication
  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;

    if (!isMsalAuthenticated) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    function accountFallbackUser(): User | null {
      const account = accounts[0];
      if (!account) return null;
      return {
        email: account.username,
        name: account.name || account.username,
        role: 'basic-user',
      };
    }

    async function fetchUserProfile() {
      try {
        const token = await getAccessToken();
        if (!token || cancelled) return;

        const response = await fetchWithRetry(`${API_BASE}/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;

        if (response.status === 401 || response.status === 403) {
          // Authoritative denial — this session gets no more than basic-user
          setUser(accountFallbackUser());
          return;
        }

        if (!response.ok) throw new Error(`Failed to fetch user profile: ${response.status}`);

        const data = await response.json();
        if (!cancelled) {
          setUser({
            email: data.email,
            name: data.name,
            role: data.role,
          });
        }
      } catch (err) {
        console.error('[AuthContext] Failed to fetch /me:', err);
        // Transient failure — keep the previously known user/role rather than
        // downgrading; only fall back to basic-user when we never had a role
        if (!cancelled) {
          setUser(prev => prev ?? accountFallbackUser());
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    fetchUserProfile();
    return () => { cancelled = true; };
  }, [isMsalAuthenticated, inProgress, accounts, getAccessToken]);

  const signIn = useCallback(() => {
    instance.loginRedirect(loginRequest).catch((err) => {
      console.error('[AuthContext] Login failed:', err);
    });
  }, [instance]);

  const signOut = useCallback(() => {
    setUser(null);
    instance.logoutRedirect().catch((err) => {
      console.error('[AuthContext] Logout failed:', err);
    });
  }, [instance]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!user,
        isLoading,
        user,
        getAccessToken,
        signIn,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

function BypassAuthProvider({ children }: { children: ReactNode }) {
  const mockUser = getMockUser();

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: true,
        isLoading: false,
        user: mockUser,
        getAccessToken: async () => 'mock-token',
        signIn: () => {},
        signOut: () => {},
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  if (isAuthBypassed()) {
    return <BypassAuthProvider>{children}</BypassAuthProvider>;
  }
  return <MsalAuthProvider>{children}</MsalAuthProvider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
